import { useHeaderScrollContentStyle } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { cn } from "@-/com.npmjs.cn";
import { useResolveClassNames } from "@-/com.npmjs.uniwind";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import {
  PRIMARY_NAVIGATION_STRIP_ACCORDION_SURFACE_COLOR_VARIABLE,
  PrimaryNavigationStripFadeInsetContext,
  usePrimaryNavigationStripSurfaceColor,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import {
  StickyAccordionHeader,
  StickyAccordionViewport,
} from "@-/dev.improve.haniverse.modules.sticky-accordion";
import {
  useIsNavigationFocused,
  usePrimaryControlsStripOpaqueChrome,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { SectionAccordionNodes } from "#section-accordion-nodes";
import { SectionAccordionContent } from "#section-accordion-content";
import { SectionAccordionStickyHeader } from "#section-accordion-sticky-header";
import { getSectionAccordionHeaderLabel } from "#get-section-accordion-header-label";
import {
  SECTION_ACCORDION_HEADER_ACTION_WIDTH,
  SECTION_ACCORDION_HEADER_TRAILING_INSET,
} from "#section-accordion-header-action";
import {
  createSectionAccordionViewport,
  SectionAccordionBodyKeyContext,
  SectionAccordionViewportContext,
} from "#section-accordion-viewport";
import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
  useRef,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import {
  ScrollView,
  StyleSheet,
  View,
  processColor,
  type ScrollViewProps,
} from "@-/com.npmjs.react-native";

type SectionAccordionStickyHeaderElement = ReactElement<
  ComponentProps<typeof SectionAccordionStickyHeader>
>;

type SectionAccordionEndReached = {
  onEndReached: () => void;
  /** In viewport heights — see `SectionAccordionNodes.onEndReachedThreshold`. */
  threshold: number;
};

type SectionAccordionTabChild = {
  /** Set on an OPEN section body whose `SectionAccordionNodes` asked for `onEndReached`. */
  endReached?: SectionAccordionEndReached;
  isSectionBody?: boolean;
  key: string;
  node: ReactNode;
  stickyHeader?: SectionAccordionStickyHeaderElement;
};

const DEFAULT_END_REACHED_THRESHOLD = 1;

function normalizeSectionAccordionTabChildren(
  children: ReactNode,
  parentKey = "root",
): SectionAccordionTabChild[] {
  const childList: SectionAccordionTabChild[] = [];

  for (const [index, child] of Children.toArray(children).entries()) {
    const childKey =
      isValidElement(child) && child.key !== null ? String(child.key) : String(index);
    const scopedKey = `${parentKey}/${childKey}`;

    if (!isValidElement<{ children?: ReactNode }>(child)) {
      childList.push({ key: scopedKey, node: child });
      continue;
    }

    if (child.type === Fragment) {
      childList.push(...normalizeSectionAccordionTabChildren(child.props.children, scopedKey));
      continue;
    }

    if (
      isValidElement<ComponentProps<typeof SectionAccordionNodes>>(child) &&
      child.type === SectionAccordionNodes
    ) {
      const props = child.props;
      const header = (
        <SectionAccordionStickyHeader
          actions={props.actions}
          count={props.count}
          iconName={props.iconName}
          // `SectionAccordionTab` reconstructs the header from a `SectionAccordionNodes` child's
          // props (it does not render the child directly), so it must forward EVERY header-facing
          // prop — `isLoading` and `isPlaceholder` included — or it is silently dropped. The
          // `isLoading` → spinner and `isPlaceholder` → "Loading …" label mappings themselves live
          // in `SectionAccordionStickyHeader`, so they stay consistent here.
          isLoading={props.isLoading}
          isOpen={props.isOpen}
          isPlaceholder={props.isPlaceholder}
          key={props.headerKey}
          onToggle={props.onToggle}
          title={props.title}
          trailing={props.trailing}
        />
      );
      childList.push({
        key: `${scopedKey}/header/${props.headerKey}`,
        node: header,
        stickyHeader: header,
      });

      if (props.isOpen) {
        childList.push({
          endReached:
            props.onEndReached === undefined
              ? undefined
              : {
                  onEndReached: props.onEndReached,
                  threshold: props.onEndReachedThreshold ?? DEFAULT_END_REACHED_THRESHOLD,
                },
          isSectionBody: true,
          key: `${scopedKey}/body/${props.bodyKey}`,
          node: (
            <View
              className={cn("border-b border-border bg-surface", props.bodyClassName)}
              key={props.bodyKey}
            >
              <SectionAccordionContent fallback={props.fallback} title={props.title}>
                {props.children}
              </SectionAccordionContent>
            </View>
          ),
        });
      }

      continue;
    }

    if (
      isValidElement<ComponentProps<typeof SectionAccordionStickyHeader>>(child) &&
      child.type === SectionAccordionStickyHeader
    ) {
      childList.push({
        key: `${scopedKey}/header`,
        node: child,
        stickyHeader: child,
      });
      continue;
    }

    childList.push({ key: scopedKey, node: child });
  }

  return childList;
}

export function SectionAccordionTab({
  children,
  className,
  contentContainerClassName,
  contentContainerStyle,
  nestedScrollEnabled = true,
  onContentSizeChange,
  onLayout,
  onScroll,
  scrollEventThrottle = 16,
  scrollView: scrollViewProp,
  stripFade = false,
  ...scrollViewProps
}: {
  children?: ReactNode;
  className?: string;
  /** Supply a specialized scroll view while retaining shared accordion behavior. */
  scrollView?: ReactElement<ScrollViewProps>;
  /** Allow the shared navigation header fade over this accordion. Off by default. */
  stripFade?: boolean;
} & Omit<
  ScrollViewProps,
  "children" | "className" | "invertStickyHeaders" | "stickyHeaderIndices"
>) {
  // Complex destructured defaults make React Compiler skip the whole viewport.
  const scrollView = scrollViewProp ?? <ScrollView />;
  const bottomTabFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  const viewportClassName = cn("flex-1 bg-surface", className);
  const hasHydrated = useHasHydrated();
  const viewportStyle = useResolveClassNames(viewportClassName);
  const borderStyle = useResolveClassNames("border-border");
  // Uniwind resolves web theme colors from the browser's CSS cascade, which is
  // unavailable during SSR. Keep the first client render equal to the server.
  const color = processColor(hasHydrated ? borderStyle.borderColor : undefined);
  const borderColor =
    typeof color === "number"
      ? "#" + (color & 0xff_ff_ff).toString(16).padStart(6, "0")
      : "#d4d4d4";
  const { backgroundColor: backgroundColorProp, ...viewportInlineStyle } =
    StyleSheet.flatten(scrollViewProps.style) ?? {};
  const resolvedBackgroundColor = hasHydrated ? viewportStyle.backgroundColor : undefined;
  const backgroundColor =
    backgroundColorProp === undefined ? resolvedBackgroundColor : backgroundColorProp;
  const tabChildList = normalizeSectionAccordionTabChildren(children);
  const headerChildList = tabChildList.filter((child) => child.stickyHeader !== undefined);
  const [viewport] = useState(createSectionAccordionViewport);
  const isNavigationFocused = useIsNavigationFocused();
  const firedHeightRef = useRef(new Map<string, number>());
  const endReachedChildren = tabChildList.filter((child) => child.endReached !== undefined);
  useLayoutEffect(() => {
    // Prune only committed sections; a concurrent render can still be abandoned.
    viewport.retainBodies(
      new Set(tabChildList.filter((child) => child.isSectionBody).map((child) => child.key)),
    );
    for (const key of firedHeightRef.current.keys()) {
      if (!endReachedChildren.some((child) => child.key === key)) {
        firedHeightRef.current.delete(key);
      }
    }
  }, [endReachedChildren, tabChildList, viewport]);
  const checkEndReached = () => {
    const { height, offset } = viewport.getViewport();
    if (!isNavigationFocused || height <= 0) {
      return;
    }
    for (const child of endReachedChildren) {
      const layout = viewport.getBody(child.key);
      if (layout === undefined || child.endReached === undefined) {
        continue;
      }
      if (
        // A section already passed is not "near the bottom". Without this lower bound, layout
        // changes from later sections can keep paging an invisible earlier section to its end.
        layout.y + layout.height < offset ||
        layout.y + layout.height - offset - height > child.endReached.threshold * height
      ) {
        continue;
      }
      if (firedHeightRef.current.get(child.key) === layout.height) {
        continue;
      }
      firedHeightRef.current.set(child.key, layout.height);
      child.endReached.onEndReached();
    }
  };
  const opaqueChrome = !stripFade && headerChildList.length > 0;
  usePrimaryControlsStripOpaqueChrome(opaqueChrome);
  // White section bodies (`bg-surface`) need a matching tab-bar fade; Learn keeps muted chrome.
  usePrimaryNavigationStripSurfaceColor(
    PRIMARY_NAVIGATION_STRIP_ACCORDION_SURFACE_COLOR_VARIABLE,
    opaqueChrome && isNavigationFocused,
  );
  // The reserved gap clears the header fade; with the fade stood down it is just a band
  // of the sticky header's bleed above the first section.
  const headerContentStyle = useHeaderScrollContentStyle({
    contentContainerClassName,
    contentContainerStyle,
    suppressContentGap: opaqueChrome,
  });
  const scrollElement = (
    <ScrollView
      {...scrollViewProps}
      className={cn(viewportClassName, "bg-transparent native:overflow-visible")}
      contentContainerClassName={cn("grow bg-surface", contentContainerClassName)}
      contentContainerStyle={headerContentStyle}
      nestedScrollEnabled={nestedScrollEnabled}
      onContentSizeChange={(width, height) => {
        onContentSizeChange?.(width, height);
        checkEndReached();
      }}
      onLayout={(event) => {
        viewport.setViewport(event.nativeEvent.layout.height);
        checkEndReached();
        onLayout?.(event);
      }}
      onScroll={(event) => {
        viewport.setViewport(
          event.nativeEvent.layoutMeasurement.height,
          event.nativeEvent.contentOffset.y,
        );
        checkEndReached();
        onScroll?.(event);
      }}
      removeClippedSubviews={false}
      scrollEventThrottle={scrollEventThrottle}
      style={viewportInlineStyle}
    >
      {tabChildList.map((child, index) => {
        const isLastBody = child.isSectionBody && index === tabChildList.length - 1;
        const node =
          isLastBody && isValidElement<{ className?: string }>(child.node)
            ? cloneElement(child.node, {
                className: cn(child.node.props.className, "grow border-b-0"),
              })
            : child.node;
        if (child.isSectionBody) {
          return (
            <View
              className={isLastBody ? "grow" : undefined}
              collapsable={false}
              key={child.key}
              onLayout={(event) => {
                const { height, y } = event.nativeEvent.layout;
                viewport.setBody(child.key, { height, y });
                checkEndReached();
              }}
            >
              <SectionAccordionBodyKeyContext value={child.key}>
                {node}
              </SectionAccordionBodyKeyContext>
            </View>
          );
        }
        if (child.stickyHeader === undefined) {
          return <Fragment key={child.key}>{node}</Fragment>;
        }
        const props = child.stickyHeader.props;
        return (
          <StickyAccordionHeader
            actions={props.actions?.map((action) => ({
              label: action.label,
              selected: action.selected,
            }))}
            actionTrailingInset={SECTION_ACCORDION_HEADER_TRAILING_INSET}
            actionWidth={SECTION_ACCORDION_HEADER_ACTION_WIDTH}
            busy={props.isLoading === true || props.isPlaceholder === true}
            className="z-10 overflow-visible"
            disabled={props.isPlaceholder === true}
            first={index === 0}
            headerKey={child.key}
            index={headerChildList.indexOf(child)}
            key={child.key}
            label={getSectionAccordionHeaderLabel(props)}
            onAction={(actionIndex) => props.actions?.[actionIndex]?.onPress()}
            onToggle={props.onToggle}
            open={props.isOpen}
          >
            <View
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              pointerEvents="none"
            >
              {index === 0 ? (
                <View
                  className="absolute inset-x-0 bottom-full h-screen bg-muted"
                  testID="section-accordion-top-overscroll-background"
                />
              ) : null}
              {cloneElement(child.stickyHeader, { interactive: false })}
            </View>
          </StickyAccordionHeader>
        );
      })}
    </ScrollView>
  );
  return (
    <View className="min-h-0 flex-1" style={{ backgroundColor, marginBottom: bottomTabFadeInset }}>
      <StickyAccordionViewport borderColor={borderColor} className="min-h-0 flex-1 overflow-hidden">
        <SectionAccordionViewportContext value={viewport}>
          {cloneElement(scrollView, scrollElement.props)}
        </SectionAccordionViewportContext>
      </StickyAccordionViewport>
    </View>
  );
}
